// Presentation only. Movement, hits, reloads and weapon timings still come from the server.
// Kept DOM-free so gait, action transitions and grip reach can be checked without WebGL.
export type Facing = 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' | 'N' | 'NE';
export type WeaponKind = 'none' | 'pistol' | 'smg' | 'rifle' | 'shotgun' | 'coilgun' | 'katana' | 'tanto' | 'naginata';
export const FACINGS: Facing[] = ['E', 'SE', 'S', 'SW', 'W', 'NW', 'N', 'NE'];
export const WALK_FRAMES = 8;
const TAU = Math.PI * 2;
const clamp = (x: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, x));
export const angleDelta = (a: number, b: number) => Math.atan2(Math.sin(a - b), Math.cos(a - b));
export function facingOf(a: number): Facing { return FACINGS[((Math.round(a / (Math.PI / 4)) % 8) + 8) % 8]; }
export function weaponKind(id: string): WeaponKind {
  if (!id) return 'none';
  if (id === 'fists' || id === 'katana') return 'katana';
  if (id === 'tanto') return 'tanto';
  if (id === 'naginata') return 'naginata'; // Shingan's polearm (M10); never a player weapon
  if (id === 'pistol' || id === 'p11') return 'pistol';
  if (id === 'smart' || id === 'k5' || id === 's10') return 'smg';
  if (id === 'vs12') return 'shotgun';
  if (id === 'charge') return 'coilgun';
  return 'rifle';
}

export interface MotionInput { x: number; y: number; a: number; weapon: string; reload?: boolean; reloadSeconds?: number; charge?: number; down?: boolean;
  /** fitted-part bits of the gun in hand (PlayerSnap.gp), drawn on the held sprite only */ parts?: number }
export interface CharacterMotion {
  x: number; y: number; t: number; initialized: boolean; distance: number; speed: number; lastMove: number;
  facing: Facing; travel: Facing; aim: number; weapon: string; kind: WeaponKind; down: boolean;
  reloadActive: boolean; reloadAt: number; reloadLeft: number; reloadTotal: number; charge: number;
  shotAt: number; swingAt: number; swingSide: number; throwAt: number; vaultAt: number;
}
export interface MotionPose {
  facing: Facing; travel: Facing; frame: number; idle: number; bob: number; aim: number; kind: WeaponKind;
  recoil: number; swing: number; swingSide: number; reload: number; throwing: number; vault: number; charge: number; down: boolean;
}
export function createMotion(): CharacterMotion {
  return { x: 0, y: 0, t: 0, initialized: false, distance: 0, speed: 0, lastMove: -Infinity,
    facing: 'S', travel: 'S', aim: Math.PI / 2, weapon: '', kind: 'none', down: false,
    reloadActive: false, reloadAt: -Infinity, reloadLeft: 0, reloadTotal: 0, charge: 0, shotAt: -Infinity, swingAt: -Infinity, swingSide: -1, throwAt: -Infinity, vaultAt: -Infinity };
}
function clearActions(m: CharacterMotion) { m.shotAt = m.swingAt = m.throwAt = m.vaultAt = m.reloadAt = -Infinity; m.reloadActive = false; m.reloadLeft = m.reloadTotal = 0; }

/** Distance along the actual path, not x+y: diagonal and backwards movement animate identically.
 *  Spawns/teleports start at rest; prediction corrections cannot accumulate a giant burst of strides. */
export function advanceMotion(m: CharacterMotion, p: MotionInput, time: number): MotionPose {
  const dt = Math.max(0, time - m.t), dx = p.x - m.x, dy = p.y - m.y, d = Math.hypot(dx, dy);
  const jump = !m.initialized || d > Math.max(24, Math.min(dt, 0.1) * 400);
  if (jump || p.down || m.down !== !!p.down) {
    m.distance = 0; m.speed = 0; m.lastMove = -Infinity;
    if (jump || p.down) clearActions(m);
  } else if (dt > 0) {
    const v = d / dt, k = 1 - Math.exp(-dt * 16);
    m.speed += (v - m.speed) * k;
    if (d > 0.025) { m.distance = (m.distance + d) % 44; m.lastMove = time; m.travel = facingOf(Math.atan2(dy, dx)); }
  }
  if (p.weapon !== m.weapon) { clearActions(m); m.weapon = p.weapon; }
  const viewAngle = FACINGS.indexOf(m.facing) * Math.PI / 4;
  // Small hysteresis prevents the body flickering across a sector boundary while the weapon keeps aiming freely.
  if (!m.initialized || Math.abs(angleDelta(p.a, viewAngle)) > Math.PI / 8 + 0.055) m.facing = facingOf(p.a);
  m.kind = weaponKind(p.weapon); m.aim = p.a; m.down = !!p.down; m.charge = clamp(p.charge ?? 0);
  // The wire protocol sends an active flag, not seconds remaining. Pace the pose from weapon data,
  // hold near completion if needed, and always obey the authoritative end/cancel flag.
  const reloading = !!p.reload && !p.down;
  if (reloading && !m.reloadActive) { m.reloadAt = time; m.reloadTotal = Math.max(0.2, p.reloadSeconds ?? 1.6); }
  m.reloadActive = reloading; m.reloadLeft = reloading ? Math.max(0, m.reloadTotal - (time - m.reloadAt)) : 0;
  m.x = p.x; m.y = p.y; m.t = time; m.initialized = true;
  return sampleMotion(m, time);
}

/** Triggered by acknowledged combat events, never the local fire button. */
export function motionEvent(m: CharacterMotion, k: string, time: number, weapon?: string) {
  if (!m.initialized || m.down) return;
  const matches = !weapon || weapon === 'npc' || weapon === m.weapon;
  if (k === 'shot' && matches) m.shotAt = time;
  if (k === 'melee' && matches && (m.kind === 'katana' || m.kind === 'tanto' || m.kind === 'naginata')) { m.swingAt = time; m.swingSide *= -1; }
  if (k === 'throw') m.throwAt = time;
  if (k === 'vault') m.vaultAt = time;
}
export function sampleMotion(m: CharacterMotion, time: number): MotionPose {
  const moving = !m.down && m.speed > 2 && time - m.lastMove < 0.1;
  const frame = moving ? 1 + Math.floor((m.distance + 1e-6) / 44 * WALK_FRAMES) % WALK_FRAMES : 0;
  const idle = Math.floor(time * 2) % 4;
  const shotAge = Math.max(0, time - m.shotAt);
  const recoil = shotAge < 0.22 ? Math.exp(-shotAge * 22) : 0;
  const swingSec = m.kind === 'tanto' ? 0.2 : m.kind === 'naginata' ? 0.4 : 0.34, swingAge = time - m.swingAt;
  const throwAge = time - m.throwAt, vaultAge = time - m.vaultAt;
  return { facing: m.facing, travel: m.travel, frame, idle,
    bob: frame ? ([2, 3, 6, 7].includes(frame) ? -0.5 : 0) : idle === 2 ? -0.5 : 0,
    aim: m.aim, kind: m.kind, recoil,
    swing: swingAge >= 0 && swingAge < swingSec ? swingAge / swingSec : -1, swingSide: m.swingSide,
    reload: m.reloadActive ? clamp((time - m.reloadAt) / m.reloadTotal, 0, 0.97) : -1,
    throwing: throwAge >= 0 && throwAge < 0.34 ? throwAge / 0.34 : -1,
    vault: vaultAge >= 0 && vaultAge < 0.42 ? Math.sin(vaultAge / 0.42 * Math.PI) : 0,
    charge: m.charge, down: m.down };
}

export interface Point { x: number; y: number }
export interface ArmPose { shoulder: Point; elbow: Point; hand: Point; near: boolean }
export interface RigPose {
  right: ArmPose; left: ArmPose; grip: Point; muzzle: Point; angle: number; kind: WeaponKind;
  weaponBack: boolean; weaponVisible: boolean; magazine?: Point; flash: number;
}
export interface WeaponShape { w: number; h: number; grip: Point; muzzle: Point; support: number; magazine: number }
// Coordinates in artwork pixels (2 per world pixel). These same anchors place the hands, barrel and shot effects.
export const WEAPON_SHAPES: Record<Exclude<WeaponKind, 'none'>, WeaponShape> = {
  pistol: { w: 23, h: 14, grip: { x: 7, y: 9 }, muzzle: { x: 21, y: 5 }, support: 0.4, magazine: 0 },
  smg: { w: 34, h: 15, grip: { x: 10, y: 9 }, muzzle: { x: 32, y: 6 }, support: 4, magazine: 2.5 },
  rifle: { w: 46, h: 15, grip: { x: 15, y: 9 }, muzzle: { x: 44, y: 6 }, support: 6.1, magazine: 3 },
  shotgun: { w: 46, h: 16, grip: { x: 14, y: 9 }, muzzle: { x: 44, y: 6 }, support: 6.1, magazine: 3 },
  coilgun: { w: 43, h: 16, grip: { x: 13, y: 10 }, muzzle: { x: 41, y: 6 }, support: 5.5, magazine: 2.5 },
  katana: { w: 48, h: 11, grip: { x: 7, y: 6 }, muzzle: { x: 46, y: 4 }, support: -1.5, magazine: 0 },
  tanto: { w: 25, h: 11, grip: { x: 5, y: 6 }, muzzle: { x: 23, y: 4 }, support: 0, magazine: 0 },
  naginata: { w: 80, h: 11, grip: { x: 26, y: 6 }, muzzle: { x: 78, y: 4 }, support: -5, magazine: 0 },
};
const point = (x: number, y: number): Point => ({ x, y });
// Eight world pixels from shoulder to wrist: fingertips rest just below the belt.
export const ARM_UPPER = 4, ARM_FOREARM = 4;
const ARM_REACH = ARM_UPPER + ARM_FOREARM - 0.01;
const lerpPoint = (a: Point, b: Point, t: number) => point(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
const ease = (t: number) => { const u = clamp(t); return u * u * (3 - 2 * u); };
export function weaponPoint(grip: Point, angle: number, x: number, y = 0): Point {
  const c = Math.cos(angle), s = Math.sin(angle); return point(grip.x + c * x - s * y, grip.y + s * x + c * y);
}
/** Two-bone arm solve. The shoulder-to-hand distance is clamped to the limb lengths; no stretching. */
export function solveArm(shoulder: Point, target: Point, upper = ARM_UPPER, lower = ARM_FOREARM, bend = 1): ArmPose {
  const dx = target.x - shoulder.x, dy = target.y - shoulder.y, raw = Math.hypot(dx, dy);
  const d = clamp(raw, Math.abs(upper - lower) + 0.01, upper + lower - 0.01);
  const a = Math.atan2(dy, dx), b = Math.acos(clamp((upper * upper + d * d - lower * lower) / (2 * upper * d), -1, 1)) * bend;
  return { shoulder, elbow: point(shoulder.x + Math.cos(a + b) * upper, shoulder.y + Math.sin(a + b) * upper),
    hand: point(shoulder.x + Math.cos(a) * d, shoulder.y + Math.sin(a) * d), near: false };
}

/** Nearest handle position that both fixed-length arms can reach. The second
 * circle is the support shoulder translated back by the weapon's grip offset. */
function sharedGrip(target: Point, right: Point, left: Point, offset: Point): Point {
  const other = point(left.x - offset.x, left.y - offset.y);
  const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
  const inside = (p: Point, c: Point) => distance(p, c) <= ARM_REACH + 1e-8;
  if (inside(target, right) && inside(target, other)) return target;
  const project = (c: Point) => {
    const d = distance(target, c);
    return d <= ARM_REACH ? target : lerpPoint(c, target, ARM_REACH / d);
  };
  const candidates = [project(right), project(other)].filter(p => inside(p, right) && inside(p, other));
  const d = distance(right, other);
  if (d > 0 && d <= 2 * ARM_REACH) {
    const mid = lerpPoint(right, other, 0.5), h = Math.sqrt(Math.max(0, ARM_REACH ** 2 - d * d / 4));
    const x = -(other.y - right.y) / d * h, y = (other.x - right.x) / d * h;
    candidates.push(point(mid.x + x, mid.y + y), point(mid.x - x, mid.y - y));
  }
  // Current weapons all have overlapping reach circles; retain a primary grip
  // if a future weapon's support anchor lies beyond the two-arm span.
  return candidates.sort((a, b) => distance(a, target) - distance(b, target))[0] ?? project(right);
}

/** Where the support hand holds each two-handed held gun (held-guns.ts family ids), in that sprite's hd px from the
 *  firing hand, east-facing, y down: under the handguard or the front of the box. One-handed pistols have none and
 *  keep the shared WEAPON_SHAPES point. Kinds without their own sprite use the family they fall back to. */
export const HELD_SUPPORT: Record<string, [number, number]> = {
  smart: [11, -2], k5: [10, -3], s10: [4, -2], v47: [12, -3], vs12: [11, -3], charge: [9, -1],
};
const KIND_HELD: Partial<Record<WeaponKind, string>> = { smg: 'smart', rifle: 'v47', shotgun: 'vs12', coilgun: 'charge' };
/** hd px per rig unit (2 * CHAR_RATIO; character-art.ts imports this module, so the number is kept here). */
export const HD_PER_RIG = 1.5;

/** `weapon` is the held-gun family or weapon id; with it the support hand uses that gun's own point (HELD_SUPPORT). */
export function rigPose(p: MotionPose, weapon?: string): RigPose {
  const a = p.aim, ca = Math.cos(a), sa = Math.sin(a), bob = p.bob;
  const held = weapon === undefined || p.kind === 'pistol' ? undefined : HELD_SUPPORT[weapon] ?? HELD_SUPPORT[KIND_HELD[p.kind] ?? ''];
  /** the support hand's point along the gun at `ang` (the held sprite flips vertically when aimed west) */
  const support = (from: Point, ang: number, s: WeaponShape) => held
    ? weaponPoint(from, ang, held[0] / HD_PER_RIG, (Math.cos(ang) < 0 ? -1 : 1) * held[1] / HD_PER_RIG)
    : weaponPoint(from, ang, s.support, p.kind === 'pistol' ? 0.7 : 0);
  const rs = point(-sa * 4.2, -18 + ca * 1.1 + bob), ls = point(sa * 4.2, -18 - ca * 1.1 + bob);
  let angle = a, grip = point(ca * (1.5 - p.recoil * 1.1), -17 + sa * 1.8 + bob - p.recoil * 0.4);
  const blade = p.kind === 'katana' || p.kind === 'tanto' || p.kind === 'naginata', shape = p.kind === 'none' ? undefined : WEAPON_SHAPES[p.kind];
  if (blade) {
    grip = point(ca * 3, -13.5 + sa * 1.5 + bob);
    angle = a - (p.kind === 'naginata' ? 1.1 : 0.85);
    // the naginata wind-up (the snapshot's wu, passed as charge): the blade drawn back over the shoulder
    if (p.kind === 'naginata' && p.charge > 0 && p.swing < 0) { angle = a - 2.3 * p.charge; grip = point(-ca * 1.5, -16 - sa * 0.5 + bob); }
    if (p.swing >= 0) {
      if (p.kind === 'tanto') {
        const lunge = Math.sin(ease(p.swing < 0.45 ? p.swing / 0.45 : 1 - (p.swing - 0.45) / 0.55) * Math.PI / 2);
        angle = a - 0.1; grip = point(ca * (3 + 3.5 * lunge), -15 + sa * (2 + 2 * lunge) + bob);
      } else {
        const u = p.swing;
        const sweep = u < 0.16 ? -1.15 - 0.3 * ease(u / 0.16)
          : u < 0.62 ? -1.45 + 2.7 * ease((u - 0.16) / 0.46)
          : 1.25 + (-0.85 * p.swingSide - 1.25) * ease((u - 0.62) / 0.38);
        angle = a + sweep * p.swingSide;
        grip = point(ca * 3.5 + Math.cos(angle) * 1.5, -14.5 + sa * 2 + Math.sin(angle) * 1.5 + bob);
      }
    }
  } else angle -= p.recoil * (p.kind === 'shotgun' ? 0.14 : 0.07);

  if (p.reload >= 0) {
    const dip = Math.sin(p.reload * Math.PI);
    angle = a - dip * 0.65; grip = point(ca * (3.5 - dip * 1.5), -15 + sa + bob);
  }
  if (shape && p.kind !== 'tanto' && p.reload < 0 && p.throwing < 0) {
    grip = sharedGrip(grip, rs, ls, support(point(0, 0), angle, shape));
  }
  let rh = grip, lh = shape ? support(grip, angle, shape) : point(ls.x, ls.y + ARM_REACH);
  if (p.kind === 'tanto') lh = point(ls.x + ca, -13.5 + bob);
  let magazine: Point | undefined;
  if (p.reload >= 0 && !blade && shape) {
    const mag = weaponPoint(grip, angle, shape.magazine, 2.5), belt = point(ls.x, -10.5 + bob);
    if (p.reload < 0.22) lh = lerpPoint(lh, mag, ease(p.reload / 0.22));
    else if (p.reload < 0.42) lh = lerpPoint(mag, belt, ease((p.reload - 0.22) / 0.2));
    else if (p.reload < 0.67) lh = lerpPoint(belt, mag, ease((p.reload - 0.42) / 0.25));
    else if (p.reload < 0.84) lh = lerpPoint(mag, weaponPoint(grip, angle, 2, -1.5), ease((p.reload - 0.67) / 0.17));
    else lh = lerpPoint(weaponPoint(grip, angle, 2, -1.5), lh, ease((p.reload - 0.84) / 0.16));
    if (p.reload >= 0.22 && p.reload < 0.67) magazine = point(lh.x, lh.y + 1);
  }
  if (p.kind === 'none') {
    const swing = p.frame ? Math.sin((p.frame - 1) / WALK_FRAMES * TAU) * 1.8 : 0;
    rh = point(rs.x + ca * swing, rs.y + ARM_REACH + sa * swing); lh = point(ls.x - ca * swing, ls.y + ARM_REACH - sa * swing);
  }
  if (p.throwing >= 0) {
    const u = p.throwing, cock = point(rs.x - ca * 2, -23 + bob), release = point(ca * 8, -18 + sa * 5 + bob);
    rh = u < 0.28 ? lerpPoint(grip, cock, ease(u / 0.28)) : u < 0.52 ? lerpPoint(cock, release, ease((u - 0.28) / 0.24)) : lerpPoint(release, grip, ease((u - 0.52) / 0.48));
    lh = point(ls.x, -12 + bob);
  }
  const hangingArm = (shoulder: Point, hand: Point) => {
    const one = solveArm(shoulder, hand, ARM_UPPER, ARM_FOREARM, 1), two = solveArm(shoulder, hand, ARM_UPPER, ARM_FOREARM, -1);
    // Elbows hang below the shoulder, instead of folding up across the neck. When level, prefer the outer elbow.
    return one.elbow.y + Math.abs(one.elbow.x) * 0.1 >= two.elbow.y + Math.abs(two.elbow.x) * 0.1 ? one : two;
  };
  const right = hangingArm(rs, rh);
  if (p.kind !== 'none' && p.throwing < 0) {
    // If a wide sword cut reaches the arm's limit, move the handle to the solved hand (never detach it).
    grip = right.hand;
    if (p.reload < 0 && shape && p.kind !== 'tanto') lh = support(grip, angle, shape);
  }
  const left = hangingArm(ls, lh);
  if (magazine) magazine = point(left.hand.x, left.hand.y + 1);
  // The nearer sleeve is drawn over the receiver; the far sleeve goes behind the torso.
  right.near = ca >= 0; left.near = ca < 0;
  const muzzle = shape ? weaponPoint(grip, angle, (shape.muzzle.x - shape.grip.x) / 2, (shape.muzzle.y - shape.grip.y) / 2) : grip;
  return { right, left, grip, muzzle, angle, kind: p.kind, weaponBack: sa < -0.35,
    weaponVisible: p.kind !== 'none' && !p.down && p.throwing < 0, magazine,
    flash: !blade && p.recoil > 0.45 && p.reload < 0 ? p.recoil : 0 };
}

/** A short afterimage of the blade's actual solved tip, rather than an unrelated arc at the player's feet. */
export function bladeTrail(p: MotionPose): Point[] {
  if (p.down || p.throwing >= 0 || p.swing < 0.16 || p.swing > 0.72 || (p.kind !== 'katana' && p.kind !== 'tanto' && p.kind !== 'naginata')) return [];
  const out: Point[] = [];
  for (let i = 4; i >= 0; i--) out.push(rigPose({ ...p, swing: Math.max(0.16, p.swing - i * 0.04) }).muzzle);
  return out;
}
